import {
  Anchor,
  Badge,
  Button,
  Card,
  Center,
  Flex,
  Grid,
  Group,
  NumberInput,
  Paper,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle, IconSearch } from '@tabler/icons-react';
import { useState } from 'react';
import useFetch from '../../helpers/useFetch';
import Spinner from '../Spinner';
import { formatMs } from './shared';

const MONO = { textTransform: 'none', fontFamily: 'var(--mantine-font-family-monospace)', fontWeight: 500 };

const ConfigChip = ({ tip, children }) => {
  const badge = (
    <Badge variant="default" size="sm">
      <span style={MONO}>{children}</span>
    </Badge>
  );
  if (!tip) return badge;
  return (
    <Tooltip label={tip} multiline maw={280} withArrow>
      {badge}
    </Tooltip>
  );
};

const TraceCardTitle = ({ children }) => (
  <Text fz={11} fw={600} c="dimmed" tt="uppercase" style={{ letterSpacing: '0.04em' }} mb={6}>
    {children}
  </Text>
);

const fmtScore = (value) => (value == null ? '—' : Number(value).toFixed(2));
const fmtFused = (value) => (value == null ? '—' : Number(value).toFixed(4));

const UnitCell = ({ leg, unitId }) =>
  leg ? (
    <>
      <Text size="xs" fw={500} lineClamp={1}>
        {leg.question || leg.title}
      </Text>
      <Text size="xs" c="dimmed" lineClamp={1}>
        {leg.snippet}
      </Text>
    </>
  ) : (
    <Text size="xs" ff="monospace">
      {unitId}
    </Text>
  );

const LegTable = ({ rows }) => (
  <Table verticalSpacing="xs" horizontalSpacing="sm">
    <Table.Thead>
      <Table.Tr>
        <Table.Th w={36}>#</Table.Th>
        <Table.Th w={72}>esScore</Table.Th>
        <Table.Th>unit</Table.Th>
      </Table.Tr>
    </Table.Thead>
    <Table.Tbody>
      {(rows || []).map((row) => (
        <Table.Tr key={row.unitId}>
          <Table.Td fz="xs">{row.rank}</Table.Td>
          <Table.Td fz="xs" ff="monospace">
            {fmtScore(row.esScore)}
          </Table.Td>
          <Table.Td>
            <UnitCell leg={row} unitId={row.unitId} />
          </Table.Td>
        </Table.Tr>
      ))}
    </Table.Tbody>
  </Table>
);

const RetrievalDebuggerScreen = ({ apiBase, defaultLocale, onOpenDocument }) => {
  const fetch = useFetch();

  const [query, setQuery] = useState('');
  const [locale, setLocale] = useState(defaultLocale || 'en');
  const [tag, setTag] = useState('');
  const [topN, setTopN] = useState(8);
  const [expandedIds, setExpandedIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [ranTopN, setRanTopN] = useState(null);

  const locales = [...new Set([defaultLocale, 'bg', 'en'].filter(Boolean))].map((value) => ({
    value,
    label: value.toUpperCase(),
  }));

  const runRetrieval = () => {
    if (!query.trim() || loading) return;
    setLoading(true);
    const body = { query: query.trim(), locale, topN: Math.min(Math.max(Math.floor(Number(topN) || 8), 1), 20) };
    if (tag.trim()) body.tagFilter = [tag.trim()];
    fetch(`${apiBase}/debugSearch`, { method: 'POST', body: JSON.stringify(body) })
      .then((response) => {
        setResult(response);
        setRanTopN(body.topN);
      })
      .catch((error) =>
        notifications.show({ title: 'Retrieval failed', message: error.message, color: 'danger' })
      )
      .finally(() => setLoading(false));
  };

  const trace = result?.trace;
  const degraded = !!result?.degraded;
  const legIndex = {};
  [...(trace?.legs?.bm25 || []), ...(trace?.legs?.knn || [])].forEach((leg) => {
    if (!legIndex[leg.unitId]) legIndex[leg.unitId] = leg;
  });
  const results = result?.results || [];
  const maxScore = Math.max(...results.map((r) => r.score || 0), 0) || 1;

  // A tool whose point is showing the exact production pipeline can't hardcode a tunable:
  // prefer what the trace reports, else fall back to what this run's largest leg actually
  // returned (labelled as such), else say nothing at all.
  const tracedCandidates = trace?.candidatesPerLeg;
  const observedCandidates = Math.max((trace?.legs?.bm25 || []).length, (trace?.legs?.knn || []).length);
  const candidatesLabel =
    tracedCandidates != null
      ? `${tracedCandidates} / leg`
      : observedCandidates > 0
        ? `${observedCandidates} / leg`
        : null;

  const toggleExpanded = (unitId) =>
    setExpandedIds((prev) => (prev.includes(unitId) ? prev.filter((id) => id !== unitId) : [...prev, unitId]));

  const boostLabel = (priority) => {
    const multiplier = 1 + (trace?.priorityBoost || 0) * (priority || 0);
    return `×${Number(multiplier.toFixed(2))}${priority > 0 ? ` (P${priority})` : ''}`;
  };

  return (
    <>
      <Card withBorder radius="sm" padding="md">
        <Flex align="center" gap={8} wrap="wrap">
          <TextInput
            placeholder="Ask what a shopper would ask…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') runRetrieval();
            }}
            style={{ flex: 1, minWidth: 280 }}
          />
          <Select
            data={locales}
            value={locale}
            onChange={(value) => value && setLocale(value)}
            allowDeselect={false}
            w={110}
          />
          <TextInput placeholder="Tag filter" value={tag} onChange={(e) => setTag(e.target.value)} w={140} />
          <Text size="xs" c="dimmed">
            Results
          </Text>
          <NumberInput value={topN} onChange={setTopN} min={1} max={20} allowDecimal={false} w={80} />
          <Button
            leftSection={<IconSearch size={15} stroke={1.5} />}
            onClick={runRetrieval}
            loading={loading}
            disabled={!query.trim()}
          >
            Run retrieval
          </Button>
        </Flex>
        <Group gap={6} mt={10} wrap="wrap">
          {degraded && (
            <Tooltip
              label="Semantic leg unavailable — results are keyword-only until the embedder recovers. No user-facing error."
              multiline
              maw={260}
              withArrow
            >
              <Badge variant="light" color="orange" size="sm">
                degraded — keyword only
              </Badge>
            </Tooltip>
          )}
          {trace && (
            <>
              <ConfigChip tip="How the keyword and semantic rankings are merged: each passage scores 1/(60 + its rank) per leg. A passage ranked well by BOTH legs beats one ranked well by either alone.">
                rrfK = {trace.rrfK}
              </ConfigChip>
              <ConfigChip
                tip={`Documents can carry a priority 0–3; the fused score is multiplied by 1 + ${trace.priorityBoost}·priority, nudging important documents up.`}
              >
                boost = 1 + {trace.priorityBoost}·P
              </ConfigChip>
              <ConfigChip tip="An optional second AI pass that re-orders the top candidates by reading them against the question. Currently disabled.">
                rerank: {trace.rerankApplied ? 'on' : 'off'}
              </ConfigChip>
              <ConfigChip tip="How many passages are handed to the assistant to answer from.">
                topN = {ranTopN}
              </ConfigChip>
              {candidatesLabel && (
                <ConfigChip
                  tip={
                    tracedCandidates != null
                      ? 'How many passages each search leg (keyword + semantic) considers before their rankings are merged.'
                      : 'How many passages the largest search leg returned on this run — the configured per-leg candidate limit is not reported in the trace, so this is a floor, not the setting.'
                  }
                >
                  candidates: {candidatesLabel}
                </ConfigChip>
              )}
            </>
          )}
        </Group>
      </Card>

      {loading ? (
        <Center py={60}>
          <Spinner />
        </Center>
      ) : !result ? (
        <Center py={60}>
          <Text size="sm" c="dimmed">
            Run a retrieval to see the passages the assistant would answer from, plus the full ranking trace.
          </Text>
        </Center>
      ) : (
        <>
          <Group justify="space-between" mt="md" mb="xs">
            <Text size="sm" fw={600}>
              Answer-ready passages
            </Text>
            <Text size="xs" c="dimmed">
              {results.length} passages · {formatMs(result.tookMs)}
            </Text>
          </Group>
          {results.length === 0 ? (
            <Center py={40}>
              <Text size="sm" c="dimmed">
                No passages matched this query.
              </Text>
            </Center>
          ) : (
            <Stack gap={8}>
              {results.map((res, i) => (
                <Paper withBorder radius="sm" p="sm" key={`${res.unitId}-${i}`}>
                  <Group gap={8} wrap="wrap">
                    <Center
                      fz={11}
                      fw={700}
                      style={{
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        background: 'var(--mantine-primary-color-light)',
                        color: 'var(--mantine-primary-color-light-color)',
                        flex: '0 0 auto',
                      }}
                    >
                      {i + 1}
                    </Center>
                    <Anchor
                      component="button"
                      type="button"
                      fz="sm"
                      fw={600}
                      onClick={() => onOpenDocument(res.citation.docId)}
                    >
                      {res.citation.title}
                    </Anchor>
                    {res.citation.page != null && (
                      <Badge variant="default" size="sm">
                        <span style={{ textTransform: 'none' }}>p. {res.citation.page}</span>
                      </Badge>
                    )}
                    <div style={{ flex: 1 }} />
                    <div
                      style={{
                        width: 70,
                        height: 6,
                        background: 'var(--mantine-color-gray-1)',
                        borderRadius: 3,
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        style={{
                          width: `${Math.round(((res.score || 0) / maxScore) * 100)}%`,
                          height: '100%',
                          background: 'var(--mantine-primary-color-filled)',
                          borderRadius: 3,
                        }}
                      />
                    </div>
                    <Tooltip
                      label={`Match strength relative to the best passage. Raw fused score: ${Number(res.score || 0).toFixed(4)} — the absolute number is an internal ranking value, only the order and relative size matter.`}
                      multiline
                      maw={280}
                      withArrow
                    >
                      <Text size="xs" fw={600} c="gray.7" w={40}>
                        {Math.round(((res.score || 0) / maxScore) * 100)}%
                      </Text>
                    </Tooltip>
                  </Group>
                  <Text
                    size="sm"
                    c="gray.7"
                    mt={6}
                    role="button"
                    tabIndex={0}
                    aria-expanded={expandedIds.includes(res.unitId)}
                    style={{ lineHeight: 1.5, cursor: 'pointer' }}
                    lineClamp={expandedIds.includes(res.unitId) ? undefined : 3}
                    onClick={() => toggleExpanded(res.unitId)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleExpanded(res.unitId);
                      }
                    }}
                  >
                    {res.unitType === 'qa' && res.question ? res.question : res.content}
                  </Text>
                </Paper>
              ))}
            </Stack>
          )}

          {trace && (
            <>
              <Text size="sm" fw={600} mt="lg" mb="xs">
                Retrieval trace
              </Text>
              <Grid gutter={10}>
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <Card withBorder radius="sm" padding="sm" h="100%">
                    <TraceCardTitle>BM25 leg · keyword</TraceCardTitle>
                    <LegTable rows={trace.legs?.bm25} />
                  </Card>
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <Card withBorder radius="sm" padding="sm" h="100%">
                    <TraceCardTitle>Semantic kNN leg · vectors</TraceCardTitle>
                    {trace.legs?.knn ? (
                      <LegTable rows={trace.legs.knn} />
                    ) : (
                      <Stack align="center" gap={6} py={24} px={12} ta="center">
                        <IconAlertTriangle size={28} stroke={1.2} color="var(--mantine-color-orange-6)" />
                        <Text size="sm" fw={600} c="gray.7">
                          Semantic leg unavailable
                        </Text>
                        <Text size="xs" c="dimmed">
                          Results were served from the keyword leg only — no user-facing error.
                        </Text>
                      </Stack>
                    )}
                  </Card>
                </Grid.Col>
                <Grid.Col span={12}>
                  <Card withBorder radius="sm" padding="sm">
                    <TraceCardTitle>Fusion · RRF + priority boost</TraceCardTitle>
                    <Table verticalSpacing="xs" horizontalSpacing="sm">
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>unit</Table.Th>
                          <Table.Th w={90}>rrfScore</Table.Th>
                          <Table.Th w={90}>priority</Table.Th>
                          <Table.Th w={90}>finalScore</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {(trace.fused || []).map((fused) => (
                          <Table.Tr key={fused.unitId}>
                            <Table.Td>
                              <UnitCell leg={legIndex[fused.unitId]} unitId={fused.unitId} />
                            </Table.Td>
                            <Table.Td fz="xs" ff="monospace">
                              {fmtFused(fused.rrfScore)}
                            </Table.Td>
                            <Table.Td fz="xs">{boostLabel(fused.priority)}</Table.Td>
                            <Table.Td fz="xs" ff="monospace" fw={600}>
                              {fmtFused(fused.finalScore)}
                            </Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                    <Text size="xs" c="dimmed" mt={6}>
                      final = RRF(bm25, knn; k={trace.rrfK}) × (1 + {trace.priorityBoost}·priority)
                    </Text>
                  </Card>
                </Grid.Col>
              </Grid>
            </>
          )}
        </>
      )}
    </>
  );
};

export default RetrievalDebuggerScreen;
